import React, { useState } from "react";
import { all_routes } from "../../router/all_routes";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import ProfileCard from "../common/profileCard";
import StudentSidebar from "../common/studentSidebar";
import { Link } from "react-router-dom";
import SettingsLinks from "./settingsLinks/settingsLinks";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import BackendImage from "../../../core/common/backendImage";
import CustomSelect from "../../../core/common/commonSelect";
import { Country, Gender, maritalStatus } from "../../../core/common/selectOption/json/selectOption";
import { DatePicker } from "antd";
import SettingsModal from "./settingsModal/settingsModal";
import { useDispatch, useSelector } from "react-redux";
import { User } from "../../../core/types/types";
import axios from "axios";
import dayjs from 'dayjs';
import { updateUser } from "../../../core/redux/authSlice";
import apiInstance from "../../../utility/apiInstance";
import { ALPHA_REGEX, stripNonAlpha, PHONE_REGEX, stripNonNumeric } from "../../../utility/inputValidators";

const StudentSettings = () => {
    const route = all_routes;
    const dispatch = useDispatch();

    const user = useSelector((state: any) => state.auth.user);

    const [msg, setMsg] = useState({
        error: false,
        loading: false,
        text: ''
    });

    const [errorField, setErrorField] = useState('');

    const [imagePreview, setImagePreview] = useState<string | null>(
        user?.image?.url || null
    );
    const [imageUploading, setImageUploading] = useState(false);

    const [formData, setFormData] = useState<User>({
        id: user?.id || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        image: user?.image?.id || user?.image || null,
        email: user?.email || '',
        username: user?.username || '',
        phone: user?.phone || '',
        role: user?.role || '',
        gender: user?.gender || '',
        dob: user?.dob ? new Date(user.dob) : new Date(),
        firstLanguage: user?.firstLanguage || '',
        countryOfCitizenship: user?.countryOfCitizenship || '',
        passport: user?.passport || '',
        passportExpiry: user?.passportExpiry || '',
        maritalStatus: user?.maritalStatus || '',
    })

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        fd.append('alt', 'Profile photo');
        try {
            setImageUploading(true);
            const res = await apiInstance.post('/api/media', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const media = res.data?.doc || res.data;
            setFormData(prev => ({ ...prev, image: media.id }));
            setImagePreview(media.url || null);
        } catch {
            setMsg({ error: true, loading: false, text: 'Image upload failed' });
        } finally {
            setImageUploading(false);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, image: null }));
        setImagePreview(null);
    };

    const getModalContainer = () => {
        const modalElement = document.getElementById("add_assignment");
        return modalElement ? modalElement : document.body;
    };

    const handleInputChange = (
        e: any
    ) => {
        const { name } = e.target;
        let { value } = e.target;
        if (typeof value === 'string' && (name === 'firstName' || name === 'lastName')) {
            value = stripNonAlpha(value);
        } else if (typeof value === 'string' && name === 'phone') {
            value = stripNonNumeric(value);
        }
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }))
        if (errorField === name) setErrorField('');
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{7,15}$/;

    const validate = () => {
        let newErrors = '';
        let invalidField = '';

        if (!formData.firstName?.trim() || !ALPHA_REGEX.test(formData.firstName)) {
            newErrors = "Valid first name is required";
            invalidField = 'firstName';
        } else if (!formData.lastName?.trim() || !ALPHA_REGEX.test(formData.lastName)) {
            newErrors = "Valid last name is required";
            invalidField = 'lastName';
        } else if (!formData.email?.trim() || !emailRegex.test(formData.email)) {
            newErrors = "Valid email is required";
            invalidField = 'email';
        } else if (!formData.username?.trim()) {
            newErrors = "Username is required";
            invalidField = 'username';
        } else if (!/^[a-zA-Z0-9_.]+$/.test(formData.username.trim())) {
            newErrors = "Username may only contain letters, numbers, underscores (_) and periods (.)";
            invalidField = 'username';
        } else if (!formData.phone?.trim() || !phoneRegex.test(formData.phone)) {
            newErrors = "Valid phone number is required";
            invalidField = 'phone';
        } else if (!formData.gender?.trim()) {
            newErrors = "Gender is required";
            invalidField = 'gender';
        } else if (!formData.maritalStatus?.trim()) {
            newErrors = "Marital status is required";
            invalidField = 'maritalStatus';
        } else if (!formData.firstLanguage?.trim()) {
            newErrors = "First language is required";
            invalidField = 'firstLanguage';
        } else if (!formData.countryOfCitizenship?.trim()) {
            newErrors = "Country of citizenship is required";
            invalidField = 'countryOfCitizenship';
        } else if (!formData.passport?.trim()) {
            newErrors = "Passport number is required";
            invalidField = 'passport';
        } else if (!formData.passportExpiry?.trim() || isNaN(new Date(formData.passportExpiry).getTime()) || new Date(formData.passportExpiry) <= new Date(new Date().toDateString())) {
            newErrors = "Passport expiry date must be a future date";
            invalidField = 'passportExpiry';
        } else if (!formData.dob || isNaN(new Date(formData.dob).getTime()) || new Date(formData.dob) >= new Date(new Date().toDateString())) {
            newErrors = "Date of birth must be a past date";
            invalidField = 'dob';
        }

        setErrorField(invalidField);

        setMsg({
            error: true,
            loading: false,
            text: newErrors
        })

        return newErrors.length === 0
    };

    const updateProfile = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        setMsg({
            error: false,
            loading: true,
            text: ''
        });

        try {
            const response = await apiInstance.patch(`/api/users/${formData.id}`, formData);
            dispatch(updateUser(response.data.doc))
            setMsg({
                error: false,
                loading: false,
                text: 'Profile updated successfully!'
            });

        } catch (error: any) {
            setMsg({
                error: true,
                loading: false,
                text: error.message
            });
        }
    }

    return (
        <>
            <div className="content">
                <div className="container">
                    {/* profile box */}
                    <ProfileCard />
                    {/* profile box */}
                    <div className="row">
                        {/* sidebar */}
                        <StudentSidebar />
                        {/* sidebar */}
                        <div className="col-lg-9">
                            <div className="mb-3">
                                <h5>Settings</h5>
                            </div>
                            <SettingsLinks />
                            <div className="card">
                                <div className="card-body">
                                    <form onSubmit={updateProfile}>
                                        <div className="profile-upload-group">
                                            <div className="d-flex align-items-center">
                                                <span className="avatar flex-shrink-0 avatar-xxxl avatar-rounded border me-3">
                                                    <BackendImage
                                                        src={imagePreview ?? undefined}
                                                        fallback="/assets/img/user/user-02.jpg"
                                                        alt="Profile"
                                                        className="img-fluid"
                                                    />
                                                </span>
                                                <div className="profile-upload-head">
                                                    <h6>Profile Photo</h6>
                                                    <p className="fs-14 mb-0">
                                                        PNG or JPG no bigger than 800px width and height
                                                    </p>
                                                    <div className="new-employee-field">
                                                        <div className="d-flex align-items-center mt-2">
                                                            <div className="image-upload position-relative mb-0 me-2">
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={handleImageUpload}
                                                                    disabled={imageUploading}
                                                                />
                                                                <Link
                                                                    to="#"
                                                                    className="btn bg-gray-100 btn-sm rounded-pill image-uploads"
                                                                >
                                                                    {imageUploading ? 'Uploading...' : 'Upload'}
                                                                </Link>
                                                            </div>
                                                            {imagePreview && (
                                                            <div className="img-delete">
                                                                <Link
                                                                    to="#"
                                                                    className="btn btn-secondary btn-sm rounded-pill"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        removeImage();
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Link>
                                                            </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="edit-profile-info mb-3">
                                                <h5 className="mb-1">Personal Details</h5>
                                                <p>Edit your personal information</p>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            First Name <span className="text-danger"> *</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errorField === 'firstName' ? 'is-invalid' : ''}`}
                                                            defaultValue=""
                                                            name="firstName"
                                                            value={formData.firstName}
                                                            onChange={handleInputChange}
                                                        />
                                                        {errorField === 'firstName' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Last Name <span className="text-danger"> *</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errorField === 'lastName' ? 'is-invalid' : ''}`}
                                                            defaultValue=""
                                                            name="lastName"
                                                            value={formData.lastName}
                                                            onChange={handleInputChange}
                                                        />
                                                        {errorField === 'lastName' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            User Name <span className="text-danger"> *</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errorField === 'username' ? 'is-invalid' : ''}`}
                                                            defaultValue=""
                                                            name="username"
                                                            value={formData.username}
                                                            onChange={handleInputChange}
                                                        />
                                                        {errorField === 'username' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Phone Number{" "}
                                                            <span className="text-danger"> *</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errorField === 'phone' ? 'is-invalid' : ''}`}
                                                            defaultValue=""
                                                            name="phone"
                                                            value={formData.phone}
                                                            onChange={handleInputChange}
                                                        />
                                                        {errorField === 'phone' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Gender <span className="text-danger"> *</span>
                                                        </label>
                                                        <CustomSelect
                                                            options={Gender}
                                                            className={`select d-flex ${errorField === 'gender' ? 'is-invalid' : ''}`}
                                                            placeholder="Select"
                                                            name="gender"
                                                            value={formData.gender}
                                                            onChange={(selectedOption) => {
                                                                handleInputChange({
                                                                    target: {
                                                                        name: 'gender',
                                                                        value: selectedOption.value
                                                                    }
                                                                });
                                                            }}
                                                        />
                                                        {errorField === 'gender' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Marital Status <span className="text-danger"> *</span>
                                                        </label>
                                                        <CustomSelect
                                                            options={maritalStatus}
                                                            className={`select d-flex ${errorField === 'maritalStatus' ? 'is-invalid' : ''}`}
                                                            placeholder="Select"
                                                            name="maritalStatus"
                                                            value={formData.maritalStatus}
                                                            onChange={(selectedOption) => {
                                                                handleInputChange({
                                                                    target: {
                                                                        name: 'maritalStatus',
                                                                        value: selectedOption.value
                                                                    }
                                                                });
                                                            }}
                                                        />
                                                        {errorField === 'maritalStatus' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            First Language <span className="text-danger"> *</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errorField === 'firstLanguage' ? 'is-invalid' : ''}`}
                                                            defaultValue=""
                                                            name="firstLanguage"
                                                            value={formData.firstLanguage}
                                                            onChange={handleInputChange}
                                                        />
                                                        {errorField === 'firstLanguage' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Country of Citizenship <span className="text-danger"> *</span>
                                                        </label>
                                                        <CustomSelect
                                                            options={Country}
                                                            className={`select d-flex ${errorField === 'countryOfCitizenship' ? 'is-invalid' : ''}`}
                                                            placeholder="Select"
                                                            name="countryOfCitizenship"
                                                            value={formData.countryOfCitizenship}
                                                            onChange={(selectedOption) => {
                                                                handleInputChange({
                                                                    target: {
                                                                        name: 'countryOfCitizenship',
                                                                        value: selectedOption.value
                                                                    }
                                                                });
                                                            }}
                                                        />
                                                        {errorField === 'countryOfCitizenship' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Passport <span className="text-danger"> *</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errorField === 'passport' ? 'is-invalid' : ''}`}
                                                            defaultValue=""
                                                            name="passport"
                                                            value={formData.passport}
                                                            onChange={handleInputChange}
                                                        />
                                                        {errorField === 'passport' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Passport Expiry <span className="text-danger"> *</span>
                                                        </label>
                                                        <div className="input-icon-start position-relative">
                                                            <span className="input-icon-addon">
                                                                <i className="isax isax-calendar" />
                                                            </span>

                                                            <DatePicker
                                                                name="passportExpiry"
                                                                className={`form-control datetimepicker ${errorField === 'passportExpiry' ? 'is-invalid' : ''}`}
                                                                getPopupContainer={getModalContainer}
                                                                placeholder="Select Date"
                                                                format="DD/MM/YYYY"
                                                                disabledDate={(current) => current && current.isBefore(dayjs().startOf('day').add(1, 'day'))}
                                                                onChange={(date) => {
                                                                    handleInputChange({
                                                                        target: {
                                                                            name: 'passportExpiry',
                                                                            value: date ? date.format('YYYY-MM-DD') : ''
                                                                        }
                                                                    });
                                                                }}
                                                                value={formData.passportExpiry ? dayjs(formData.passportExpiry) : null}
                                                            />
                                                        </div>
                                                        {errorField === 'passportExpiry' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            DOB <span className="text-danger"> *</span>
                                                        </label>
                                                        <div className="input-icon-start position-relative">
                                                            <span className="input-icon-addon">
                                                                <i className="isax isax-calendar" />
                                                            </span>

                                                            <DatePicker
                                                                name="dob"
                                                                className={`form-control datetimepicker ${errorField === 'dob' ? 'is-invalid' : ''}`}
                                                                getPopupContainer={getModalContainer}
                                                                placeholder="Select Date"
                                                                format="DD/MM/YYYY"
                                                                disabledDate={(current) => current && !current.isBefore(dayjs().startOf('day'))}
                                                                onChange={(date) => {
                                                                    handleInputChange({
                                                                        target: {
                                                                            name: 'dob',
                                                                            value: date ? date.format('YYYY-MM-DD') : ''
                                                                        }
                                                                    });
                                                                }}
                                                                value={formData.dob ? dayjs(formData.dob) : null}
                                                            />
                                                        </div>
                                                        {errorField === 'dob' && (
                                                            <div className="invalid-feedback d-block">{msg.text}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <button
                                                        className="btn btn-secondary rounded-pill"
                                                        type="submit"
                                                    >
                                                        Update Profile
                                                    </button>
                                                    {msg.loading &&
                                                        <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">Updating profile...</span>
                                                    }
                                                    {!msg.loading && !errorField &&
                                                        <span className={`mt-3 ${msg.error ? 'text-red' : ''} fs-14 fw-normal d-flex align-items-center justify-content-center`}>{msg.text}</span>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            {/* <div className="card mb-0">
                                <div className="card-body">
                                    <h5 className="fs-18 mb-3">Delete Account</h5>
                                    <h6 className="mb-1">
                                        Are you sure you want to delete your account?
                                    </h6>
                                    <p className="mb-3">
                                        Refers to the action of permanently removing a user's
                                        account and associated data from a system, service and
                                        platform.
                                    </p>
                                    <Link
                                        to="#"
                                        className="btn btn-secondary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#delete_account"
                                    >
                                        Delete Account
                                    </Link>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
            <SettingsModal />
        </>
    );
};

export default StudentSettings;
